import { useEffect, useRef } from 'react';

export type ImageLightboxItem = {
  fileName: string;
  downloadUrl: string;
};

export function isImageAttachment(contentType: string): boolean {
  return contentType.startsWith('image/');
}

export function isPdfAttachment(contentType: string): boolean {
  return contentType === 'application/pdf';
}

export function fileKindLabel(contentType: string): string {
  if (isPdfAttachment(contentType)) {
    return 'PDF';
  }
  if (isImageAttachment(contentType)) {
    return 'Image';
  }
  return 'Text';
}

export function FileAttachmentIcon({ label }: { label: string }) {
  return (
    <div
      aria-hidden="true"
      style={{
        width: '44px',
        height: '56px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#1e3a5f',
        fontFamily: 'system-ui, sans-serif',
        boxSizing: 'border-box',
        flexShrink: 0,
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="36"
        height="36"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
      <span style={{ fontSize: '10px', fontWeight: 700, marginTop: '2px', lineHeight: 1 }}>{label}</span>
    </div>
  );
}

export function ImageLightbox({
  item,
  onClose,
}: {
  item: ImageLightboxItem;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Image preview for ${item.fileName}`}
      onClick={onClose}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          onClose();
        }
      }}
      tabIndex={-1}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.82)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          position: 'relative',
          maxWidth: 'min(1100px, 100%)',
          maxHeight: '100%',
          background: '#e2e8f0',
          borderRadius: '16px',
          padding: '20px 20px 16px',
          boxSizing: 'border-box',
          boxShadow: '0 24px 80px rgba(15, 23, 42, 0.35)',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close image preview"
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '36px',
            height: '36px',
            border: 0,
            borderRadius: '999px',
            background: 'rgba(15, 23, 42, 0.78)',
            color: '#f8fafc',
            fontSize: '22px',
            lineHeight: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            cursor: 'pointer',
          }}
        >
          ×
        </button>
        <div
          style={{
            fontFamily: 'system-ui, sans-serif',
            fontWeight: 700,
            color: '#0f172a',
            marginBottom: '12px',
            paddingRight: '40px',
            overflowWrap: 'anywhere',
          }}
        >
          {item.fileName}
        </div>
        <img
          src={item.downloadUrl}
          alt={item.fileName}
          style={{
            display: 'block',
            maxWidth: 'min(1024px, 100%)',
            maxHeight: 'calc(100vh - 160px)',
            borderRadius: '12px',
            background: '#ffffff',
          }}
        />
      </div>
    </div>
  );
}
